import {
  GridColDef,
  GridFilterModel,
  GridSortModel,
  GridValueFormatterParams,
} from "@mui/x-data-grid";
import {
  ServerDataGrid,
  useGridFilters,
  usePagination,
  useSorting,
} from "components/ui/ServerDataGrid";
import { OrderStatus } from "constants/index";
import dayjs from "dayjs";
import { gridModelsToQuery } from "features/api";
import { useListOrdersQuery } from "features/trading/accounts";

// The filter panel emits a change on open, seeding an empty item, and again on
// every keystroke. Comparing what the request would actually carry -- via the
// same mapping that builds it -- keeps that noise from costing a refetch or a
// page reset, while an empty quick filter reads as no filter at all.
const asRequest = (model?: GridFilterModel) => {
  const { search, ...fields } = gridModelsToQuery({ gridFilters: model }) ?? {};

  return JSON.stringify({ ...fields, search: search?.filter(Boolean) ?? [] });
};

// A column's field is the key the serializer emits, and for the three joined
// columns that key is an alias: the row carries custodian__name, while the term
// the API orders by is account__custodian__name. Wiring the sort model straight
// through would send the alias, and an unrecognised ordering term is dropped
// rather than rejected -- a sorted-looking header over unsorted rows, which
// reads as the same bug this page is fixing. Translate those three on the way
// into the request; the grid keeps its own field names, which is what it
// matches rows against.
const ORDERING_FIELDS: Record<string, string> = {
  account__number: "account__account_number",
  client__name: "account__client__name",
  custodian__name: "account__custodian__name",
};

// The eight columns sorting was asked for. Every other header stays inert: the
// API accepts an ordering term for exactly these, and a header that moves but
// changes nothing reads as broken rather than as unsupported.
const SORTABLE_FIELDS = new Set([
  "account__number",
  "asset__symbol",
  "client__name",
  "created_on",
  "custodian__name",
  "ext_order_id",
  "filled_price",
  "status",
]);

const toApiSorting = (sorting: GridSortModel): GridSortModel =>
  sorting.map((item) => ({
    ...item,
    field: ORDERING_FIELDS[item.field] ?? item.field,
  }));

export const OrdersList = () => {
  const [pagination, setPagination] = usePagination();
  const [gridFilters, setGridFilters] = useGridFilters();
  // Newest first, matching both the order the page has always opened in and the
  // API's own default -- so the header arrow states what the rows actually do.
  const [sorting, setSorting] = useSorting("created_on", "desc");

  const { data, isFetching } = useListOrdersQuery({
    pagination,
    sorting: toApiSorting(sorting),
    gridFilters,
  });

  const columns: GridColDef[] = [
    {
      field: "created_on",
      headerName: "Created At",
      valueFormatter: ({ value }: GridValueFormatterParams) =>
        dayjs.utc(value).tz("America/New_York").format("MM/DD/YYYY hh:mm:ss A"),
      minWidth: 200,
    },
    {
      field: "ext_order_id",
      headerName: "Ext. ID",
      width: 200,
      filterable: false,
    },
    {
      field: "asset__symbol",
      headerName: "Symbol",
      width: 220,
      filterable: false,
    },
    {
      field: "client__name",
      headerName: "Client Name",
      width: 250,
      filterable: false,
    },
    {
      field: "account__number",
      headerName: "Account #",
      width: 150,
      filterable: false,
    },
    {
      field: "custodian__name",
      headerName: "Custodian",
      width: 150,
      filterable: false,
    },
    {
      field: "filled_price",
      headerName: "Filled price",
      // The sort arrow and the column menu take ~68px out of the header, which
      // at the 100px default leaves 32px for a label that needs 78 -- "Filled
      // price" renders as "Fil...". Only the sortable columns without an
      // explicit width hit this; 150 matches the widths already used here.
      width: 150,
      filterable: false,
    },
    {
      field: "price",
      headerName: "Price",
      filterable: false,
    },
    {
      field: "confirmed_quantity",
      headerName: "Confirmed qty",
      width: 150,
      filterable: false,
    },
    {
      field: "filled_qty",
      headerName: "Filled qty",
      filterable: false,
    },
    {
      field: "quantity",
      headerName: "Qty",
      filterable: false,
    },
    {
      field: "type",
      headerName: "Type",
      filterable: false,
    },
    {
      field: "side",
      headerName: "Side",
      filterable: false,
    },
    {
      field: "status",
      headerName: "Status",
      type: "singleSelect",
      // As above: sorted, the 100px default clips "Status" to "St...".
      width: 150,
      valueOptions: () => Object.values(OrderStatus),
    },
    {
      field: "status_message",
      headerName: "Status message",
      width: 150,
      filterable: false,
    },
  ].map((col) => ({
    ...col,
    sortable: SORTABLE_FIELDS.has(col.field),
    valueFormatter: col.valueFormatter
      ? col.valueFormatter
      : ({ value }: GridValueFormatterParams) =>
          value === null ? "N/A" : value,
  }));

  return (
    <ServerDataGrid
      columns={columns}
      rows={data?.results || []}
      rowCount={data?.count || 0}
      loading={isFetching}
      paginationModel={pagination}
      sortModel={sorting}
      onSortModelChange={setSorting}
      onPaginationModelChange={setPagination}
      // Deliberately no filterModel prop: the grid holds that state itself, and
      // controlling it makes clearing the quick filter fire the request twice.
      onFilterModelChange={(filters: GridFilterModel) => {
        if (asRequest(gridFilters) === asRequest(filters)) {
          return;
        }

        setGridFilters(filters);
        // A narrowed result set makes the current page index meaningless --
        // requesting page 50 of a two-page response returns nothing.
        setPagination((current) =>
          current.page === 0 ? current : { ...current, page: 0 },
        );
      }}
    />
  );
};
